/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';

// Tier pricing. CAP ships a single plan ($50 Basic); the "CAP Plus" add-on
// (+$20) is what turns it into Plus ($70). Tier is therefore driven by the
// presence of a subscription add-on (and the resulting total price), not by the
// plan UID — the single plan's UID is the same for Basic and Plus.
export const BASIC_PRICE = 50;
export const PLUS_PRICE = 70;

export interface PlanInfo {
	name: string;
	price: number;
	interval: 'mo' | 'yr';
	isPlus: boolean;
	uid: string | undefined;
	renewalDate: string | null; // formatted "MMM D, YYYY"
	renewalMoment: moment.Moment | null;
	status: string | null;
}

/**
 * Extract plan / billing info for a company from its subscription record
 * (company.Subscription[0]). All values come from the Outseta subscription
 * object stored on the profile. NOTE: the DB/profile field names still carry the
 * legacy `zoho*` prefix (zohoSubscriptionPlan / zohoProductId), but the JSON they
 * hold is the Outseta subscription object — Plan.Name / Plan.MonthlyRate /
 * Plan.AnnualRate / BillingRenewalTerm / RenewalDate. The plan-UID heuristic is
 * only a last resort when Outseta hasn't populated a value.
 */
export const getPlanInfo = (subscription: any): PlanInfo => {
	const sub = subscription?.zohoSubscriptionPlan || {}; // Outseta subscription object
	const outsetaPlan = sub?.Plan || {};
	const uid = subscription?.zohoProductId || outsetaPlan?.Uid;

	// interval — from Outseta BillingRenewalTerm, or inferred from which rate exists
	const term = sub?.BillingRenewalTerm;
	const isAnnual =
		(typeof term === 'string' && /annual|year/i.test(term)) ||
		(!!outsetaPlan?.AnnualRate && !outsetaPlan?.MonthlyRate);
	const interval: 'mo' | 'yr' = isAnnual ? 'yr' : 'mo';

	// price — real Outseta plan rate PLUS any add-on rates. The CAP Plus add-on
	// ($20) is what makes a $50 plan a $70 Plus subscription, so its rate must be
	// included for the tier/price to be correct.
	const outsetaRate = isAnnual
		? outsetaPlan?.AnnualRate
		: outsetaPlan?.MonthlyRate;
	const addOns: any[] = sub?.SubscriptionAddOns || [];
	const hasAddOn = addOns.length > 0;
	const addOnTotal = addOns.reduce((total: number, sa: any) => {
		const addOn = sa?.AddOn || {};
		const rate = isAnnual ? addOn?.AnnualRate : addOn?.MonthlyRate;
		return total + Number(rate ?? 0);
	}, 0);

	let price = Number(outsetaRate ?? 0) + addOnTotal;
	if (!price || Number.isNaN(price)) {
		// last-resort fallback when Outseta hasn't populated rates: an add-on
		// present means Plus, otherwise Basic.
		price = hasAddOn ? PLUS_PRICE : BASIC_PRICE;
	}

	// tier derived from the add-on presence / total price (Basic is the base plan)
	const isPlus = hasAddOn || price >= PLUS_PRICE;
	const name = isPlus ? 'Plus' : 'Basic';

	// renewal / next billing date — Outseta RenewalDate (mirrored to expiresAt)
	const rawRenewal = sub?.RenewalDate || subscription?.expiresAt || null;
	const renewalMoment = rawRenewal ? moment(rawRenewal) : null;
	const renewalDate =
		renewalMoment && renewalMoment.isValid()
			? renewalMoment.format('MMM D, YYYY')
			: null;

	const status =
		subscription?.status || sub?.Account?.AccountStageLabel || null;

	return {
		name,
		price,
		interval,
		isPlus,
		uid,
		renewalDate,
		renewalMoment,
		status,
	};
};

/** Monthly-equivalent amount, for totalling mixed monthly/annual plans. */
export const monthlyEquivalent = (info: PlanInfo): number =>
	info.interval === 'yr' ? info.price / 12 : info.price;
